import { currentLang, t } from './i18n';
import {
  cachedAllGoals, setCachedAllGoals,
  cachedEvents, setCachedEvents,
  hasLoadedGoals,
  currentDeadlineFilter, currentStatusFilter, currentMainTab,
  doneGoalsHasMore,
} from './state';
import { api } from './api';
import { formatDateJP, isOverdue, parseDeadlineDate, getLocalToday } from './helpers';
import { filterGoalsByDeadline, renderFilterBar } from './filters';
import type { CalendarEvent, GoalWithSubtasks } from './types';
import type { GoalListSectionRenderModel } from './components/shared';
import { isGoalCompleting } from './goals-actions';
import { renderWorkList } from './work-list';
import {
  isGoalActiveSurface,
  isGoalReviewSurface,
  isGoalShownOnNormalSurface,
} from './simplified-intake-ui';
function isShownOnNormalSurface(goal: GoalWithSubtasks) {
  return isGoalShownOnNormalSurface(goal);
}

function isActiveNormalGoal(goal: GoalWithSubtasks) {
  return isGoalActiveSurface(goal, isGoalCompleting(goal.id));
}

function isUserReviewWaiting(goal: GoalWithSubtasks) {
  return isGoalReviewSurface(goal);
}

function mountGoalList(container: HTMLElement | null, sections: GoalListSectionRenderModel[], moreLabel = '') {
  if (!container) return;
  const listEl = document.createElement('zen-goal-list') as HTMLElement & { sections?: GoalListSectionRenderModel[]; moreLabel?: string };
  listEl.sections = sections;
  listEl.moreLabel = moreLabel;
  container.replaceChildren(listEl);
}

export function renderGoalGrid(container: HTMLElement | null, goals: GoalWithSubtasks[]) {
  mountGoalList(container, [{
    icon: '',
    label: '',
    count: 0,
    goals,
  }]);
}

function mountTodayEvents(container: HTMLElement | null, events: CalendarEvent[], meetingMinutes: number, doneLabel: string) {
  if (!container) return;
  const eventsEl = document.createElement('zen-today-events') as HTMLElement & { events?: CalendarEvent[]; meetingMinutes?: number; doneLabel?: string };
  eventsEl.events = events;
  eventsEl.meetingMinutes = meetingMinutes;
  eventsEl.doneLabel = doneLabel;
  container.replaceChildren(eventsEl);
}

// #2749: the first tab is the list of tasks, judgments and waits the server
// derives (GET /api/work-list); see work-list.ts.
export function renderToday() {
  renderWorkList();
}

export function isEventDone(ev: CalendarEvent, now: Date) {
  if (ev.is_all_day) return !!ev.done_at;
  const autoEnded = !!(ev.end_at && new Date(ev.end_at) < now);
  return autoEnded || !!ev.done_at;
}

export async function toggleEventDone(id: string) {
  const ev = cachedEvents.find(e => e.id === id);
  if (!ev) return;
  const newDone = !ev.done_at;
  ev.done_at = newDone ? new Date().toISOString() : null;
  renderTodayEvents();
  try {
    await api(`/api/events/${id}`, { method: 'PATCH', json: { done: newDone } });
  } catch {
    ev.done_at = newDone ? null : new Date().toISOString();
    renderTodayEvents();
  }
}

export function renderTodayEvents() {
  const section = document.getElementById('todayEventsSection');
  if (!section) return;
  if (cachedEvents.length === 0) { section.style.display = 'none'; return; }

  const now = new Date();
  let mtgMinutes = 0;
  for (const ev of cachedEvents) {
    if (!ev.is_all_day && ev.start_at && ev.end_at) {
      mtgMinutes += Math.round((new Date(ev.end_at).getTime() - new Date(ev.start_at).getTime()) / 60000);
    }
  }

  mountTodayEvents(
    document.getElementById('todayEventsList'),
    cachedEvents,
    mtgMinutes,
    String(t('completedItems', { n: cachedEvents.filter(ev => isEventDone(ev, now)).length })),
  );
  section.style.display = '';
}

export function toggleEventsDoneSection(btn: HTMLElement) {
  const list = btn.nextElementSibling as HTMLElement | null;
  if (!list) return;
  const isOpen = list.style.display !== 'none';
  list.style.display = isOpen ? 'none' : '';
  const icon = btn.querySelector<HTMLElement>('.mi');
  if (icon) icon.textContent = isOpen ? 'expand_more' : 'expand_less';
}

export async function loadTodayEvents() {
  const today = new Date();
  const ymd = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}`;
  const data = await api(`/api/events?date_min=${ymd}T00:00:00%2B09:00&date_max=${ymd}T23:59:59%2B09:00`);
  setCachedEvents((data.events || []).filter(ev => ev.status !== 'cancelled'));
  cachedEvents.sort((a, b) => new Date(a.start_at).getTime() - new Date(b.start_at).getTime());
  renderTodayEvents();
}

export function renderAll() {
  // #2856: the goal cards are shown on 完了 only, and drawn when it opens.
  // Drawing every open goal's card behind 今日 made each choice wait for it.
  if (currentMainTab !== 'done') return;
  const container = document.getElementById('allList');
  const empty = document.getElementById('allEmpty');

  let statusFiltered = cachedAllGoals.filter(g => isShownOnNormalSurface(g));
  if (currentStatusFilter === 'active') statusFiltered = statusFiltered.filter(isActiveNormalGoal);
  // #2739: the done list is read a page at a time, most recently completed
  // first, so it is shown in that order and older pages follow below.
  else if (currentStatusFilter === 'done') statusFiltered = sortByLatestDone(statusFiltered.filter(g => g.status === 'done'));
  else if (currentStatusFilter === 'review') statusFiltered = statusFiltered.filter(g => g.status !== 'done' && isUserReviewWaiting(g));

  const goals = filterGoalsByDeadline(statusFiltered, currentDeadlineFilter);
  const moreLabel = currentStatusFilter === 'done' && doneGoalsHasMore ? String(t('loadMoreDone')) : '';


  if (!hasLoadedGoals) {
    empty.style.display = 'none';
    return;
  }

  if (goals.length === 0 && moreLabel) {
    // The loaded page has none for this deadline filter, but older ones may.
    empty.style.display = 'none';
    mountGoalList(container, [], moreLabel);
    return;
  }

  if (goals.length === 0) {
    container.replaceChildren();
    empty.style.display = 'block';
    const onboarding = document.getElementById('emptyOnboarding');
    const congrats = document.getElementById('emptyCongrats');
    const filtered = document.getElementById('emptyFiltered');
    if (cachedAllGoals.length === 0) {
      onboarding.style.display = '';
      congrats.style.display = 'none';
      filtered.style.display = 'none';
    } else if (cachedAllGoals.every(g => g.status === 'done')) {
      onboarding.style.display = 'none';
      congrats.style.display = '';
      filtered.style.display = 'none';
    } else {
      onboarding.style.display = 'none';
      congrats.style.display = 'none';
      filtered.style.display = '';
    }
    return;
  }

  empty.style.display = 'none';

  mountGoalList(container, [{ icon: '', label: '', count: 0, goals }], moreLabel);
}

function sortByLatestDone(goals: GoalWithSubtasks[]) {
  return goals.slice().sort((a, b) => String(b.done_at ?? '').localeCompare(String(a.done_at ?? '')));
}

export function renderGoalViews() {
  renderToday();
  renderAll();
  updateFilterCounts();
}

export function updateFilterCounts() {
  const allGoals = cachedAllGoals.filter(isShownOnNormalSurface);

  const activeCount = allGoals.filter(isActiveNormalGoal).length;
  const reviewCount = allGoals.filter(g => g.status !== 'done' && isUserReviewWaiting(g)).length;
  const doneCount = allGoals.filter(g => g.status === 'done').length;
  let statusFiltered = allGoals;
  if (currentStatusFilter === 'active') statusFiltered = statusFiltered.filter(isActiveNormalGoal);
  else if (currentStatusFilter === 'done') statusFiltered = statusFiltered.filter(g => g.status === 'done');
  else if (currentStatusFilter === 'review') statusFiltered = statusFiltered.filter(g => g.status !== 'done' && isUserReviewWaiting(g));

  const counts = {
    all: statusFiltered.length,
    today: filterGoalsByDeadline(statusFiltered, 'today').length,
    tomorrow: filterGoalsByDeadline(statusFiltered, 'tomorrow').length,
    week: filterGoalsByDeadline(statusFiltered, 'week').length,
    month: filterGoalsByDeadline(statusFiltered, 'month').length,
    overdue: filterGoalsByDeadline(statusFiltered, 'overdue').length,
    none: filterGoalsByDeadline(statusFiltered, 'none').length,
  };

  renderFilterBar({
    ...counts,
    active: activeCount,
    review: reviewCount,
    done: doneCount,
  });
}
